import { Checkbox } from "@mantine/core";
import {
  LIST_UNKNOWN, keepsUnknown, listHasKnown, listKeepsUnknown, noneWords, rangeSet, withKeep, withListKeep,
  type Filters, type Measure,
} from "../lib/campaignFilters";

/* The box under a filter that keeps or leaves out the properties with no
 * value, with how many of the properties the other criteria match that is.
 * Under a range (James, 2026-10-20, engine migration 275), shown while a
 * bound is set, and under a list whose column can be not known, such as
 * property type, shown while a known value is ticked (James, 2026-10-22).
 * With nothing set those properties are already on the list.
 *
 * The number is the consequence of the box, so it is in the label rather than
 * a help line: ticking it adds exactly those properties to the count. */
export function UnknownBox({ filters, measure, count, failed, onChange }: {
  filters: Filters;
  measure: Measure;
  /** How many matching properties have no value, or undefined while counting. */
  count: number | null | undefined;
  failed?: boolean;
  onChange: (next: Filters) => void;
}) {
  if (!rangeSet(filters, measure)) return null;
  return (
    <NoValueBox none={noneWords(measure, filters)} count={count} failed={failed}
                checked={keepsUnknown(filters, measure)}
                onChange={(keep) => onChange(withKeep(filters, measure, keep))} />
  );
}

/** The same box under a list, for the criterion it names. */
export function ListUnknownBox({ filters, listKey, count, failed, onChange }: {
  filters: Filters;
  listKey: string;
  count: number | null | undefined;
  failed?: boolean;
  onChange: (next: Filters) => void;
}) {
  const u = LIST_UNKNOWN[listKey];
  if (!u || !listHasKnown(filters, listKey)) return null;
  return (
    <NoValueBox none={u.none} count={count} failed={failed}
                checked={listKeepsUnknown(filters, listKey)}
                onChange={(keep) => onChange(withListKeep(filters, listKey, keep))} />
  );
}

function NoValueBox({ none, count, failed, checked, onChange }: {
  none: string;
  count: number | null | undefined;
  failed: boolean | undefined;
  checked: boolean;
  onChange: (keep: boolean) => void;
}) {
  const label = typeof count === "number" && count > 0
    ? `Include the ${count.toLocaleString()} ${count === 1 ? "property" : "properties"} with ${none}`
    : `Include properties with ${none}`;
  return (
    <Checkbox
      mt="xs"
      checked={checked}
      label={label}
      description={failed ? "The count did not load." : count === 0 ? "None match the other criteria." : undefined}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  );
}
